<template>
  <div>
    <!-- Organization Tabs Component -->
    <OrganizationTabs
      :initial-organization-id="initialOrganizationId"
      @organization-selected="handleOrganizationSelected"
      @organizations-loaded="handleOrganizationsLoaded"
      ref="organizationTabsRef"
    />

    <!-- Organization Header -->
    <div v-if="selectedOrganization" class="list-header">
      <div class="title-stack">
        <ImageThumbnail
          v-if="selectedOrganization.logo_url"
          :url="selectedOrganization.logo_url"
          max-height="100px"
        />
        <h1>{{ selectedOrganization.name }}</h1>
      </div>
      <div class="spacer"></div>
    </div>

    <!-- Channel Selector -->
    <LoadingIndicator v-if="isLoading">Loading channels...</LoadingIndicator>
    <div v-else-if="channels?.length > 1" class="tabs">
      <button 
        v-for="channel in channels" 
        :key="channel.id"
        :class="{ 'active': channel.id === selectedChannel?.id }" 
        @click="selectChannel(channel.id)"
      >
        {{ channel.name }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { getOrganizationChannels, organizationKeys } from '@/scripts/api/organizationApi';
import ImageThumbnail from '@/components/ImageThumbnail.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import OrganizationTabs from '@/components/OrganizationTabs.vue';
import { handleApiError } from '@/scripts/shared-utils';

const props = defineProps({
  // Optional initial organization ID to select
  initialOrganizationId: {
    type: String,
    default: null
  },
  // Optional initial channel ID to select
  initialChannelId: {
    type: String,
    default: null
  }
});

const emit = defineEmits([
  'organization-selected', 
  'channel-selected',
  'organizations-loaded',
  'channels-loaded'
]);

const queryClient = useQueryClient();

const organizationTabsRef = ref(null);
const selectedChannel = ref(null);

// Computed property for organization selection state
const isOrganizationSelected = computed(() => !!organizationTabsRef.value?.selectedOrganization?.id);

// Computed property for the selected organization
const selectedOrganization = computed(() => organizationTabsRef.value?.selectedOrganization);

// Computed property for organization channels query key
const organizationChannelsQueryKey = computed(() => 
  organizationKeys.getOrganizationChannels(selectedOrganization.value?.id)
);

// Query for channels
const { data: channels, isLoading } = useQuery({
  queryKey: organizationChannelsQueryKey,
  queryFn: () => getOrganizationChannels(selectedOrganization.value?.id),
  enabled: isOrganizationSelected,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching channels')
});

watch(channels, (newChannels) => {
  if (newChannels && newChannels.length > 0) {
    emit('channels-loaded', newChannels);
    
    // If initialChannelId is provided and exists in the list, select it
    if (props.initialChannelId) {
      const initialChannel = newChannels.find(channel => channel.id === props.initialChannelId);
      if (initialChannel) {
        selectedChannel.value = initialChannel;
        emit('channel-selected', initialChannel);
        return;
      }
    }
    
    // If no initial channel specified or not found, select the first one
    selectedChannel.value = newChannels[0];
    emit('channel-selected', newChannels[0]);
  } else {
    selectedChannel.value = null;
    emit('channel-selected', null);
  }
});

const handleOrganizationSelected = (organization) => {
  emit('organization-selected', organization);
};

const handleOrganizationsLoaded = (organizations) => {
  emit('organizations-loaded', organizations);
};

const selectChannel = (id) => {
  const channel = channels.value.find(channel => channel.id === id) || null;
  selectedChannel.value = channel;
  emit('channel-selected', channel);
};

const refreshChannelData = () => {
  if (selectedOrganization.value) {
    queryClient.invalidateQueries({ queryKey: organizationKeys.getOrganizationChannels(selectedOrganization.value.id) });
  }
};

defineExpose({
  selectedOrganization,
  selectedChannel,
  organizations: computed(() => organizationTabsRef.value?.organizations),
  channels,
  isLoadingOrganizations: computed(() => organizationTabsRef.value?.isLoadingOrganizations),
  isLoadingChannels: isLoading,
  refreshChannelData,
  refreshOrganizationData: () => organizationTabsRef.value?.refreshOrganizationData()
});
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/shared-tabs.css';
</style>
